import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SettingsIcon from "@mui/icons-material/Settings";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { useParams } from "react-router-dom";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import { exportClientInvoicingCsv, fetchClientInvoicingReport, searchReportCustomers } from "../../../../services/reports";
import ReportRepAsyncSelect from "../shared/ReportRepAsyncSelect";
import ReportColumnVisibilityButton from "../shared/ReportColumnVisibilityButton";
import ReportSavedReportsButton from "../shared/ReportSavedReportsButton";
import { normalizeHiddenColumns as normalizeSavedHiddenColumns } from "../shared/savedReportUtils";
import { UITableShell, UITableToolbar } from "../../../../components/ui";
import {
  reportContentSx,
  reportFilterCardSx,
  reportFilterGridSx,
  reportPageWrapperSx,
  reportShellSx,
  reportSummaryCardSx,
  reportTableBodySx,
  reportTableContainerSx,
  reportTableHeaderCellSx,
  reportTablePaperSx,
  reportTableSectionSx,
} from "../shared/reportLayout";
import { buildClientInvoicingSalesOrderUrl } from "./salesOrderUrl";
import { getClientInvoicingAmountColumn } from "./amountColumn";
import {
  CLIENT_INVOICING_STATUS_COLUMN_LABELS,
  CLIENT_INVOICING_STATUS_OPTIONS,
  getClientInvoicingFiltersForUnifiedStatus,
  getClientInvoicingReportRequestFilters,
  getClientInvoicingUnifiedStatus,
  normalizeClientInvoicingStatusFilters,
} from "./clientInvoicingFilters";

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString(undefined, {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const DEFAULT_FILTERS = {
  dateFrom: "",
  dateTo: "",
  client: "",
  clientId: "",
  repId: "",
  quickFind: "",
  soStatus: "ready_to_invoice",
  paymentStatus: "all",
};

const DEFAULT_SUMMARY = {
  totalReadyForInvoicing: 0,
  totalEstimatedInvoiceAmount: 0,
  totalUninvoicedAmount: 0,
  clientCount: 0,
  overdueCount: 0,
  partiallyInvoicedCount: 0,
  partiallyPaidCount: 0,
};

const getColumnValue = (row, field) => field.split(".").reduce((value, key) => value?.[key], row);
const customerOptionLabel = (option) => option?.label || option?.businessName || option?.name || "";
const normalizeAvatarUrl = (imgSrc) => {
  if (!imgSrc) return null;
  if (Array.isArray(imgSrc)) {
    const selectedPic = imgSrc.find((pic) => pic?.selected) || imgSrc[0];
    return normalizeAvatarUrl(selectedPic?.url || null);
  }
  if (typeof imgSrc === "string") {
    if (/^https?:\/\//i.test(imgSrc)) return imgSrc;
    if (imgSrc.startsWith("/")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}${imgSrc}`;
    if (imgSrc.startsWith("uploads")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${imgSrc}`;
    try {
      const parsed = JSON.parse(imgSrc);
      if (Array.isArray(parsed)) return normalizeAvatarUrl(parsed);
    } catch {
      // Keep raw fallback below.
    }
  }
  return imgSrc;
};
const repDisplayName = (rep) => {
  const name = [rep?.first_name || rep?.firstName, rep?.last_name || rep?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || rep?.email || (rep?.id != null ? `User ${rep.id}` : "");
};
const normalizeSavedCustomerOption = (customer) => {
  if (!customer) return null;
  const id = customer?.id != null && String(customer.id).trim() !== "" ? String(customer.id) : "";
  const label = customer?.label || customer?.businessName || customer?.name || customer?.customerName || customer?.emailAddress || (id ? `Customer #${id}` : "");
  if (!label && !id) return null;
  return {
    id: id || null,
    label,
    businessName: customer?.businessName || label,
    emailAddress: customer?.emailAddress || "",
  };
};
const normalizeSavedRepOption = (rep) => {
  if (!rep) return null;
  const id = rep?.id != null && String(rep.id).trim() !== "" ? String(rep.id) : "";
  const normalized = {
    id: id || null,
    first_name: rep?.first_name || rep?.firstName || "",
    last_name: rep?.last_name || rep?.lastName || "",
    firstName: rep?.firstName || rep?.first_name || "",
    lastName: rep?.lastName || rep?.last_name || "",
    email: rep?.email || "",
    profilePicture: rep?.profilePicture || null,
    avatar: rep?.avatar || null,
    photoUrl: rep?.photoUrl || null,
    role: rep?.role || "",
    roleLabel: rep?.roleLabel || "",
    label: rep?.label || "",
  };
  const fallbackLabel = repDisplayName(normalized);
  if (!normalized.id && !fallbackLabel) return null;
  normalized.label = normalized.label || fallbackLabel;
  return normalized;
};
const repInitials = (name) =>
  String(name || "")
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const RepCell = ({ rep, fallback = "—" }) => {
  const name = repDisplayName(rep);
  if (!rep || !name) return fallback;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Avatar
        src={normalizeAvatarUrl(rep?.profilePicture || rep?.avatar || rep?.photoUrl) || undefined}
        alt={name}
        sx={{ width: 26, height: 26, fontSize: 11 }}
      >
        {repInitials(name)}
      </Avatar>
      <Box
        component="span"
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Box>
    </Box>
  );
};

const statusChipSx = (value) => {
  const status = String(value || "").toLowerCase();
  if (status.includes("paid") && !status.includes("unpaid") && !status.includes("partial")) {
    return {
      color: "var(--ec-status-success-text)",
      backgroundColor: "var(--ec-status-success-bg)",
      borderColor: "var(--ec-status-success-border)",
    };
  }
  if (status.includes("ready")) {
    return {
      color: "var(--ec-status-accent-text, #5b21b6)",
      backgroundColor: "var(--ec-status-accent-bg, #f5f3ff)",
      borderColor: "var(--ec-status-accent-border, #c4b5fd)",
    };
  }
  if (status.includes("overdue") || status.includes("unpaid")) {
    return {
      color: "var(--ec-status-error-text)",
      backgroundColor: "var(--ec-status-error-bg)",
      borderColor: "var(--ec-status-error-border)",
    };
  }
  if (status.includes("partial")) {
    return {
      color: "var(--ec-status-warning-text)",
      backgroundColor: "var(--ec-status-warning-bg)",
      borderColor: "var(--ec-status-warning-border)",
    };
  }
  return {
    color: "var(--ec-status-neutral-text)",
    backgroundColor: "var(--ec-status-neutral-bg)",
    borderColor: "var(--ec-status-neutral-border)",
  };
};

const StatusChip = ({ value }) => (
  <Chip
    label={value || "—"}
    size="small"
    variant="outlined"
    sx={{
      height: 24,
      fontSize: "0.72rem",
      fontWeight: 650,
      borderRadius: 1.25,
      ...statusChipSx(value),
    }}
  />
);

const ReportCustomerAsyncSelect = ({
  instanceUniqueId,
  value,
  onChange,
  seedOptions = [],
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState(Array.isArray(seedOptions) ? seedOptions : []);
  const [loading, setLoading] = useState(false);
  const requestSeq = useRef(0);

  useEffect(() => {
    setInputValue(value ? customerOptionLabel(value) : "");
  }, [value]);

  useEffect(() => {
    setOptions((previous) => {
      const map = new Map(previous.map((option) => [String(option?.id || ""), option]));
      (Array.isArray(seedOptions) ? seedOptions : []).forEach((option) => {
        if (!option) return;
        map.set(String(option?.id || ""), option);
      });
      if (value?.id) map.set(String(value.id), value);
      return Array.from(map.values()).filter((option) => option && option.id != null);
    });
  }, [seedOptions, value]);

  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(async () => {
      const requestId = ++requestSeq.current;
      setLoading(true);
      try {
        const res = await searchReportCustomers({
          instanceUniqueId,
          q: inputValue,
          page: 1,
          limit: 25,
        });
        if (requestId !== requestSeq.current) return;
        const customers = Array.isArray(res?.customers) ? res.customers : [];
        setOptions((previous) => {
          const map = new Map();
          customers.forEach((customer) => {
            if (!customer?.id) return;
            map.set(String(customer.id), {
              id: customer.id,
              businessName: customer.businessName || customer.name || customer.customerName || customer.emailAddress || `Customer #${customer.id}`,
              emailAddress: customer.emailAddress || "",
              label: customer.businessName || customer.name || customer.customerName || customer.emailAddress || `Customer #${customer.id}`,
            });
          });
          (Array.isArray(seedOptions) ? seedOptions : []).forEach((option) => {
            if (!option?.id || map.has(String(option.id))) return;
            map.set(String(option.id), option);
          });
          if (value?.id && !map.has(String(value.id))) map.set(String(value.id), value);
          return Array.from(map.values());
        });
      } catch (error) {
        if (requestId !== requestSeq.current) return;
        console.error("Failed to search report customers", error);
      } finally {
        if (requestId === requestSeq.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [open, inputValue, instanceUniqueId, seedOptions, value]);

  return (
    <Autocomplete
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={value}
      disabled={disabled}
      loading={loading}
      filterOptions={(x) => x}
      inputValue={inputValue}
      onInputChange={(_, nextValue, reason) => {
        if (reason === "reset") {
          setInputValue(value ? customerOptionLabel(value) : "");
          return;
        }
        if (reason === "clear") {
          setInputValue("");
          return;
        }
        setInputValue(nextValue);
      }}
      onChange={(_, option) => {
        setInputValue(option ? customerOptionLabel(option) : "");
        onChange(option || null);
      }}
      isOptionEqualToValue={(option, currentValue) => String(option?.id || "") === String(currentValue?.id || "")}
      getOptionLabel={customerOptionLabel}
      renderOption={(props, option) => (
        <li {...props} key={option?.id || option?.label}>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Typography variant="body2" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {customerOptionLabel(option)}
            </Typography>
            {option?.emailAddress ? (
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                {option.emailAddress}
              </Typography>
            ) : null}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Client"
          placeholder="Search customers"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const ReportTable = ({ rows = [], columns = [], emptyMessage = "No data available." }) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeColumns = Array.isArray(columns) ? columns : [];

  if (!safeRows.length) {
    return (
      <Paper elevation={0} sx={{ py: 6, px: 3, textAlign: "center", borderRadius: 2, border: "1px solid", borderColor: "divider", backgroundColor: "#fafbfc" }}>
        <ReceiptLongOutlinedIcon aria-hidden="true" sx={{ fontSize: 30, color: "text.disabled" }} />
        <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700 }}>No invoicing items found</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>{emptyMessage}</Typography>
      </Paper>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        ...reportTableContainerSx,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "auto",
      }}
    >
      <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
        <TableHead>
          <TableRow>
            {safeColumns.map((column) => (
              <TableCell
                key={column.field}
                align={column.align || "left"}
                sx={reportTableHeaderCellSx({ minWidth: column.minWidth || 130 })}
              >
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}>
                  {column.headerIcon || null}
                  {column.headerName}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody sx={reportTableBodySx}>
          {safeRows.map((row, index) => (
            <TableRow
              key={row?.id || row?.orderId || index}
              hover
              sx={{ "&:hover": { backgroundColor: "rgba(15, 23, 42, 0.025) !important" } }}
            >
              {safeColumns.map((column) => {
                const value = getColumnValue(row, column.field);
                const rendered = typeof column.renderCell === "function" ? column.renderCell({ value, row }) : value || "—";
                return (
                  <TableCell
                    key={column.field}
                    align={column.align || "left"}
                    sx={{
                      whiteSpace: "nowrap",
                      verticalAlign: "middle",
                      py: 1,
                      fontVariantNumeric: column.align === "right" ? "tabular-nums" : "normal",
                    }}
                  >
                    {rendered}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ClientInvoicingReport = () => {
  const { instanceUniqueId } = useParams();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [repOptions, setRepOptions] = useState([]);
  const [selectedRep, setSelectedRep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const reportRequestSeq = useRef(0);
  const skipNextAutoLoadRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem("eliteCore:reports:clientInvoicing:columns");
      const parsed = raw ? JSON.parse(raw) : [];
      setHiddenColumns(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      setHiddenColumns([]);
    }
  }, []);

  const load = useCallback(async (filtersOverride = null) => {
    const requestId = ++reportRequestSeq.current;
    const effectiveFilters = filtersOverride && typeof filtersOverride === "object"
      ? filtersOverride
      : filters;
    const normalizedEffectiveFilters = getClientInvoicingReportRequestFilters(effectiveFilters);
    setLoading(true);
    try {
      const res = await fetchClientInvoicingReport({ instanceUniqueId, ...normalizedEffectiveFilters });
      if (requestId !== reportRequestSeq.current) return;
      setRows(Array.isArray(res?.rows) ? res.rows : []);
      setSummary(res?.summary && typeof res.summary === "object" ? { ...DEFAULT_SUMMARY, ...res.summary } : DEFAULT_SUMMARY);
      const nextOptions = Array.isArray(res?.customerOptions) ? res.customerOptions : [];
      setCustomerOptions(nextOptions);
      const nextRepOptions = Array.isArray(res?.repOptions) ? res.repOptions : [];
      setRepOptions(nextRepOptions);
      if (!normalizedEffectiveFilters.clientId) {
        setSelectedCustomer(null);
      } else {
        const matched = nextOptions.find((option) => String(option?.id || "") === String(normalizedEffectiveFilters.clientId));
        setSelectedCustomer((previous) => {
          if (matched) return matched;
          if (String(previous?.id || "") === String(normalizedEffectiveFilters.clientId)) return previous;
          return previous;
        });
      }
      if (!normalizedEffectiveFilters.repId) {
        setSelectedRep(null);
      } else {
        const matchedRep = nextRepOptions.find((option) => String(option?.id || "") === String(normalizedEffectiveFilters.repId));
        setSelectedRep((previous) => {
          if (matchedRep) return matchedRep;
          if (String(previous?.id || "") === String(normalizedEffectiveFilters.repId)) return previous;
          return previous;
        });
      }
    } catch (err) {
      if (requestId !== reportRequestSeq.current) return;
      console.error("Failed to load client invoicing report", {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
      });
      setRows([]);
      setSummary(DEFAULT_SUMMARY);
      setCustomerOptions([]);
      setRepOptions([]);
    } finally {
      if (requestId === reportRequestSeq.current) {
        setLoading(false);
      }
    }
  }, [filters, instanceUniqueId]);

  useEffect(() => {
    if (skipNextAutoLoadRef.current) {
      skipNextAutoLoadRef.current = false;
      return;
    }
    load();
  }, [load]);

  const columns = useMemo(() => {
    const amountColumn = getClientInvoicingAmountColumn(filters.soStatus);
    return [
      { field: "clientName", headerName: "Customer", minWidth: 180 },
      { field: "projectTitle", headerName: "Project / Order", minWidth: 200 },
      {
        field: "salesOrderNumber",
        headerName: "Sales Order #",
        renderCell: ({ value, row }) => {
          const url = buildClientInvoicingSalesOrderUrl(instanceUniqueId, row);
          const label = value || "—";
          if (!url) return label;
          return (
            <Button
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              size="small"
              endIcon={<OpenInNewIcon aria-hidden="true" sx={{ fontSize: "0.9rem !important" }} />}
              sx={{ textTransform: "none", minWidth: 0, px: 0.5, fontWeight: 700, gap: 0.25 }}
            >
              {label}
            </Button>
          );
        },
      },
      { field: "salesOrderStatus", headerName: CLIENT_INVOICING_STATUS_COLUMN_LABELS.salesOrderStatus, minWidth: 170, renderCell: ({ value }) => <StatusChip value={value} /> },
      { field: "paymentCoverageStatus", headerName: CLIENT_INVOICING_STATUS_COLUMN_LABELS.paymentCoverageStatus, minWidth: 150, headerIcon: <ReceiptLongOutlinedIcon aria-hidden="true" sx={{ fontSize: 15 }} />, renderCell: ({ value }) => <StatusChip value={value} /> },
      { field: "invoiceCount", headerName: "Invoice Count", align: "right" },
      { field: "totalInvoiceAmount", headerName: "Invoiced Total", align: "right", renderCell: (p) => formatCurrency(p.value) },
      { field: "totalPaidAmount", headerName: "Paid Amount", align: "right", renderCell: (p) => formatCurrency(p.value) },
      { ...amountColumn, align: "right", renderCell: (p) => formatCurrency(p.value) },
      {
        field: "clientRep",
        headerName: "Client Rep",
        minWidth: 180,
        renderCell: ({ row }) => <RepCell rep={row.clientRepDetail} fallback={row.clientRep || "—"} />,
      },
      {
        field: "orderRep",
        headerName: "Order Rep",
        minWidth: 180,
        renderCell: ({ row }) => <RepCell rep={row.orderRepDetail} fallback={row.orderRep || "—"} />,
      },
    ];
  }, [filters.soStatus, instanceUniqueId]);

  useEffect(() => {
    const validColumnKeys = new Set(columns.map((column) => String(column.field || column.headerName || "")));
    setHiddenColumns((previous) => {
      const next = previous.filter((key) => validColumnKeys.has(String(key)));
      return next.length >= validColumnKeys.size ? [] : next;
    });
  }, [columns]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("eliteCore:reports:clientInvoicing:columns", JSON.stringify(hiddenColumns));
  }, [hiddenColumns]);

  const visibleColumns = useMemo(
    () => columns.filter((column) => !hiddenColumns.includes(String(column.field || column.headerName || ""))),
    [columns, hiddenColumns],
  );
  const currentSavedReportState = useMemo(() => ({
    version: 1,
    filters: {
      dateFrom: filters.dateFrom || "",
      dateTo: filters.dateTo || "",
      client: filters.client || "",
      clientId: filters.clientId || "",
      repId: filters.repId || "",
      quickFind: filters.quickFind || "",
      soStatus: filters.soStatus || "ready_to_invoice",
      paymentStatus: filters.paymentStatus || "all",
    },
    selectedCustomer: normalizeSavedCustomerOption(selectedCustomer),
    selectedRep: normalizeSavedRepOption(selectedRep),
    hiddenColumns: Array.isArray(hiddenColumns) ? hiddenColumns.filter(Boolean) : [],
  }), [filters, hiddenColumns, selectedCustomer, selectedRep]);

  const applySavedReportState = useCallback(async (savedState = {}) => {
    const savedFilters = savedState?.filters && typeof savedState.filters === "object" ? savedState.filters : {};
    const savedCustomer = normalizeSavedCustomerOption(savedState?.selectedCustomer)
      || normalizeSavedCustomerOption(savedFilters?.clientId ? { id: savedFilters.clientId, label: savedFilters.client || `Customer #${savedFilters.clientId}` } : null);
    const savedRep = normalizeSavedRepOption(savedState?.selectedRep)
      || normalizeSavedRepOption(savedFilters?.repId ? { id: savedFilters.repId, label: `User ${savedFilters.repId}` } : null);
    const validColumnKeys = new Set(columns.map((column) => String(column.field || column.headerName || "")));

    const nextHiddenColumns = normalizeSavedHiddenColumns({
      savedState,
      validColumnKeys: Array.from(validColumnKeys),
      fallbackHiddenColumns: [],
    });

    setSelectedCustomer(savedCustomer);
    setSelectedRep(savedRep);
    if (savedCustomer?.id) {
      setCustomerOptions((previous) => {
        if (previous.some((option) => String(option?.id || "") === String(savedCustomer.id))) return previous;
        return [...previous, savedCustomer];
      });
    }
    if (savedRep?.id) {
      setRepOptions((previous) => {
        if (previous.some((option) => String(option?.id || "") === String(savedRep.id))) return previous;
        return [...previous, savedRep];
      });
    }
    const nextFilters = normalizeClientInvoicingStatusFilters({
      ...DEFAULT_FILTERS,
      ...savedFilters,
      client: savedCustomer ? customerOptionLabel(savedCustomer) : String(savedFilters?.client || ""),
      clientId: savedCustomer?.id ? String(savedCustomer.id) : "",
      repId: savedRep?.id ? String(savedRep.id) : "",
      quickFind: String(savedFilters?.quickFind || ""),
      dateFrom: String(savedFilters?.dateFrom || ""),
      dateTo: String(savedFilters?.dateTo || ""),
      soStatus: String(savedFilters?.soStatus || "ready_to_invoice"),
      paymentStatus: String(savedFilters?.paymentStatus || ""),
    });

    setHiddenColumns(nextHiddenColumns);
    skipNextAutoLoadRef.current = true;
    setFilters(nextFilters);
    await load(nextFilters);
  }, [columns, load]);

  const onExport = async () => {
    const response = await exportClientInvoicingCsv({
      instanceUniqueId,
      ...getClientInvoicingReportRequestFilters(filters),
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "client-invoicing-report.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const cards = [
    ["Ready for Invoicing", summary?.totalReadyForInvoicing ?? 0],
    ["Clients", summary?.clientCount ?? 0],
    ["Overdue", summary?.overdueCount ?? 0],
    ["Partially Paid", summary?.partiallyPaidCount ?? 0],
  ];

  return (
    <div className="instance-dashboard">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <Box className="fade-in-effect-usability" sx={reportPageWrapperSx}>
          <UITableShell customLayout paperSx={reportShellSx}>
            <UITableToolbar sx={{ backgroundColor: "background.paper", flexShrink: 0 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ width: "100%" }}>
                <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      color: "var(--ec-status-info-text)",
                      backgroundColor: "var(--ec-status-info-bg)",
                      border: "1px solid var(--ec-status-info-border)",
                    }}
                  >
                    <ReceiptLongOutlinedIcon aria-hidden="true" sx={{ fontSize: 20 }} />
                  </Box>

                  <Box>
                    <Typography variant="h6" fontWeight={750} sx={{ color: "text.primary", lineHeight: 1.15 }}>
                      Client Invoicing
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      Review sales orders that are ready for client invoicing.
                    </Typography>
                  </Box>

                  <Chip
                    label={loading ? "Loading..." : `${Array.isArray(rows) ? rows.length : 0} total`}
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-neutral-bg)",
                      color: "var(--ec-status-neutral-text)",
                      border: "1px solid var(--ec-status-neutral-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />

                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ReportSavedReportsButton
                    instanceUniqueId={instanceUniqueId}
                    reportKey="clientInvoicing"
                    currentState={currentSavedReportState}
                    onApplyReport={applySavedReportState}
                  />
                  <ReportColumnVisibilityButton
                    columns={columns}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                  />
                  <Tooltip title="Refresh report">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Refresh client invoicing report"
                        onClick={() => load()}
                        disabled={loading}
                        sx={{ color: "text.secondary" }}
                      >
                        <RefreshIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Row density">
                    <span>
                      <IconButton size="small" disabled sx={{ color: "text.secondary" }}>
                        <SettingsIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: "center" }} />

                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disableElevation
                    startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    onClick={onExport}
                    sx={{
                      fontWeight: 700,
                      textTransform: "none",
                      height: 34,
                      px: 2,
                      boxShadow: "none",
                      "&:hover": { boxShadow: "none" },
                    }}
                  >
                    Export CSV
                  </Button>
                </Stack>
              </Stack>
            </UITableToolbar>
            <Box sx={{ ...reportContentSx, minWidth: 0, maxWidth: "100%" }}>
              <Paper elevation={0} sx={reportFilterCardSx}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.25 }}>
                  <FilterAltOutlinedIcon aria-hidden="true" sx={{ fontSize: 18, color: "text.secondary" }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Report filters</Typography>
                    <Typography variant="caption" color="text.secondary">Refine the invoicing queue by date, client, representative, or status.</Typography>
                  </Box>
                </Stack>
                <Box sx={reportFilterGridSx}>
                  <TextField size="small" type="date" label="Date From" value={filters.dateFrom} onChange={(e) => setFilters((p) => ({ ...p, dateFrom: e.target.value }))} InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonthOutlinedIcon aria-hidden="true" sx={{ fontSize: 17, color: "text.secondary" }} /></InputAdornment> }} />
                  <TextField size="small" type="date" label="Date To" value={filters.dateTo} onChange={(e) => setFilters((p) => ({ ...p, dateTo: e.target.value }))} InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonthOutlinedIcon aria-hidden="true" sx={{ fontSize: 17, color: "text.secondary" }} /></InputAdornment> }} />
                  <ReportCustomerAsyncSelect
                    instanceUniqueId={instanceUniqueId}
                    value={selectedCustomer}
                    seedOptions={customerOptions}
                    onChange={(customer) => {
                      setSelectedCustomer(customer);
                      setFilters((previous) => ({
                        ...previous,
                        clientId: customer?.id ? String(customer.id) : "",
                        client: customer ? customerOptionLabel(customer) : "",
                      }));
                    }}
                  />
                  <ReportRepAsyncSelect
                    instanceUniqueId={instanceUniqueId}
                    value={selectedRep}
                    roles={["client-rep", "order-rep", "administrator", "admin"]}
                    seedOptions={repOptions}
                    label="Rep"
                    onChange={(rep) => {
                      setSelectedRep(rep);
                      setFilters((previous) => ({
                        ...previous,
                        repId: rep?.id ? String(rep.id) : "",
                      }));
                    }}
                  />
                  <TextField size="small" label="Quick Find" placeholder="SO, project, client..." value={filters.quickFind} onChange={(e) => setFilters((p) => ({ ...p, quickFind: e.target.value }))} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon aria-hidden="true" sx={{ fontSize: 18, color: "text.secondary" }} /></InputAdornment> }} />
                  <TextField select size="small" label="Status" value={getClientInvoicingUnifiedStatus(filters)} onChange={(e) => setFilters((p) => getClientInvoicingFiltersForUnifiedStatus(e.target.value, p))} SelectProps={{ displayEmpty: true }}>
                    {CLIENT_INVOICING_STATUS_OPTIONS.map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Paper>

              <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", md: "repeat(4,minmax(0,1fr))" } }}>
                {cards.map(([label, value]) => (
                  <Paper key={label} elevation={0} sx={reportSummaryCardSx}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{label}</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{value}</Typography>
                  </Paper>
                ))}
              </Box>

              <Box sx={{ ...reportTableSectionSx, minWidth: 0, maxWidth: "100%" }}>
                <Paper elevation={0} sx={{ ...reportTablePaperSx, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                  <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
                    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "auto", overflowY: "hidden" }}>
                      {loading ? (
                        <Box sx={{ py: 7, px: 3, textAlign: "center" }} role="status">
                          <CircularProgress size={28} thickness={4} />
                          <Typography variant="subtitle2" sx={{ mt: 1.5, fontWeight: 700 }}>Loading invoicing queue</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>Fetching the latest sales orders and invoice balances.</Typography>
                        </Box>
                      ) : (
                        <ReportTable rows={Array.isArray(rows) ? rows : []} columns={visibleColumns} emptyMessage="No client invoicing items found for selected filters." />
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Box>
          </UITableShell>
        </Box>
      </div>
    </div>
  );
};

export default ClientInvoicingReport;
